import { useEffect, useRef } from "react";
import {
  pingReviewSessionActivity,
  setReviewSessionState,
  pauseReviewSessionOnPageClose,
  getSessionConflict,
} from "../../../../services/case-manager/readingReviewSession";
import { APP_LOGOUT_EVENT } from "../../../../utils/logout";

const PING_INTERVAL_MS = 60 * 1000;
const IDLE_CHECK_MS = 15 * 1000;
const INPUT_EVENTS = ["mousedown", "mousemove", "keydown", "wheel", "scroll", "touchstart"];

// SCRUM-850: keeps the reviewer's stint alive and pauses it the way the API
// contract asks - while `enabled` (the reviewer holds the session):
//   - POST activity/ at most once a minute, and only after real input, so an
//     untouched tab still goes idle;
//   - pause "inactive" after idle_timeout_minutes without input;
//   - pause "left_tab" when the browser tab is hidden or this screen unmounts
//     (another My Tasks tab, another route);
//   - keepalive pause "page_closed" when the page is closed/reloaded or the
//     user logs out.
// onPaused(reason, idleSeconds) fires after an automatic pause (idleSeconds:
// time since the last input, for "inactive"); onConflict(conflict) when a
// ping finds the stint already ended (409 session_paused/session_closed).
export default function useSessionActivity({ accId, session, enabled, onPaused, onConflict }) {
  const sessionId = session?.id;
  const idleMinutes = session?.idle_timeout_minutes || 15;
  const lastInputRef = useRef(Date.now());
  const lastPingRef = useRef(0);
  const pausedRef = useRef(false);
  const callbacksRef = useRef({ onPaused, onConflict });
  callbacksRef.current = { onPaused, onConflict };

  useEffect(() => {
    if (!enabled || !accId || !sessionId) return undefined;
    pausedRef.current = false;
    lastInputRef.current = Date.now();
    lastPingRef.current = Date.now();

    const pause = (reason) => {
      if (pausedRef.current) return;
      pausedRef.current = true;
      const idleSeconds =
        reason === "inactive" ? Math.floor((Date.now() - lastInputRef.current) / 1000) : 0;
      setReviewSessionState(accId, sessionId, "pause", reason)
        .catch(() => {})
        .finally(() => callbacksRef.current.onPaused?.(reason, idleSeconds));
    };

    const onInput = () => {
      if (pausedRef.current) return;
      const now = Date.now();
      lastInputRef.current = now;
      if (now - lastPingRef.current < PING_INTERVAL_MS) return;
      lastPingRef.current = now;
      pingReviewSessionActivity(accId, sessionId).catch((error) => {
        const conflict = getSessionConflict(error);
        if (conflict) {
          pausedRef.current = true;
          callbacksRef.current.onConflict?.(conflict);
        }
      });
    };

    const idleTimer = setInterval(() => {
      if (Date.now() - lastInputRef.current >= idleMinutes * 60 * 1000) pause("inactive");
    }, IDLE_CHECK_MS);

    const onVisibility = () => {
      if (document.visibilityState === "hidden") pause("left_tab");
    };

    const onPageHide = () => {
      if (pausedRef.current) return;
      pausedRef.current = true;
      pauseReviewSessionOnPageClose(accId, sessionId);
    };

    INPUT_EVENTS.forEach((evt) => window.addEventListener(evt, onInput, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener(APP_LOGOUT_EVENT, onPageHide);

    return () => {
      clearInterval(idleTimer);
      INPUT_EVENTS.forEach((evt) => window.removeEventListener(evt, onInput, { capture: true }));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener(APP_LOGOUT_EVENT, onPageHide);
      // Leaving the screen (or the session) while still holding it.
      if (!pausedRef.current) {
        pausedRef.current = true;
        setReviewSessionState(accId, sessionId, "pause", "left_tab").catch(() => {});
      }
    };
  }, [enabled, accId, sessionId, idleMinutes]);

  // Lets the workspace mark a pause it made itself (Pause Session button)
  // so unmounting doesn't send a second one.
  return {
    markPaused: () => {
      pausedRef.current = true;
    },
  };
}
